import {useResourceManagement} from "../../hooks/resourceHooks";
import {
    getFieldsAroundIrritile,
    getIrritileById,
    getIrritileFields,
    getIrritileOrders,
    getIrritilesAroundIrritile,
} from "../../services/irritilesService";
import {Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import {createFeatureCollection} from "../../utils/mapUtils";
import {FeatureCollection, MultiPolygon, Polygon, GeoJsonProperties} from "geojson";
import {MapDataType} from "../../types/mapDataType";
import {IrritilesMap} from "../map/IrritilesMap";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {KeyValuePair} from "../base/KeyValuePair";
import {Irritile} from "../../types/domain/irritiles";
import {Order} from "../../types/domain/order";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import dayjs from "dayjs";
import {formatShortDateTime} from "../../utils/dateUtils";
import {Field} from "../../types/domain/field";
import {useCompanies} from "../../hooks/companiesHooks";

export const AdminIrriTileDetailsView: React.FC = () => {
    const {t} = useTranslation();
    const [geojson, setGeojson] = useState<FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> | null>(null);
    const [irritile, setIrritile] = useState<Irritile>();
    const [orders, setOrders] = useState<Order[]>();
    const [fields, setFields] = useState<Field[]>([]);
    const {dateFormat} = useUserPreferences();
    const irritilesManagement = useResourceManagement({
        getIrritileById,
        getIrritileFields,
        getIrritileOrders,
        getFieldsAroundIrritile,
        getIrritilesAroundIrritile,
    });
    const irritileId = useParams().irritileId;
    const companies = useCompanies();

    useEffect(() => {
        void (async () => {
            const id = irritileId!;
            const irritile = await irritilesManagement.getIrritileById(id);
            const fields = await irritilesManagement.getIrritileFields(id);
            const orders = await irritilesManagement.getIrritileOrders(id);
            const fieldsAround = await irritilesManagement.getFieldsAroundIrritile(id);
            const irritilesAround = await irritilesManagement.getIrritilesAroundIrritile(id);

            const features = [
                {
                    id: irritile.id,
                    staticTileId: irritile.staticTileId,
                    name: "irritile",
                    geometry: irritile.extent,
                    color: "rgba(125, 0, 150, 1)",
                    timezone: irritile.utmZone,
                },
                ...fields.map((field) => ({
                    id: field.id,
                    name: "fields",
                    geometry: field.geometry,
                    label: field.name,
                    color: "rgba(251, 255, 0, 1)",
                    staticTileId: undefined,
                })),
                ...fieldsAround.map((field) => ({
                    id: field.id,
                    name: "fields",
                    geometry: field.geometry,
                    label: field.name,
                    color: "blue",
                    staticTileId: undefined,
                })),
                ...irritilesAround.map((ir) => ({
                    id: ir.id,
                    staticTileId: ir.staticTileId,
                    name: "irritile",
                    timezone: ir.utmZone,
                    geometry: ir.extent,
                    color: "#00CC00",
                })),
            ];

            setIrritile(irritile);
            setOrders(orders);
            setFields(
                fields.sort((a, b) => {
                    const aOrder = a.orderId ?? "";
                    const bOrder = b.orderId ?? "";
                    if (!aOrder) return 1;
                    if (!bOrder) return -1;
                    return aOrder.localeCompare(bOrder);
                }),
            );
            setGeojson(createFeatureCollection(features));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [irritileId]);

    return (
        <Stack direction="column" sx={{height: "100%"}} gap="0.5rem">
            <IrritilesMap
                geojson={geojson}
                showRealign={true}
                showSlider={true}
                dataType={MapDataType.IRRITILE}
                enablePopup={true}
            />
            <Stack sx={{height: "30rem", padding: "1rem", overflow: "auto"}}>
                <Grid container>
                    <Grid item xs={3}>
                        <Stack direction="column" gap="0.5rem">
                            <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                                {t("admin.irritile_details_view.irritile")}
                            </Typography>
                            <Stack padding="1rem">
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.id")}>
                                        <Typography fontSize={14}>{irritile?.id}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.static_tile_id")}>
                                        <Typography fontSize={14}>{irritile?.staticTileId ?? "n/c"}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.state")}>
                                        <Typography fontSize={14}>
                                            {irritile?.isActive
                                                ? t("admin.irritile_details_view.active")
                                                : t("admin.irritile_details_view.inactive")}
                                        </Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.is_historic")}>
                                        <Typography fontSize={14}>
                                            {irritile?.isHistoric?.toString() ?? "n/c"}
                                        </Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.utmzone")}>
                                        <Typography fontSize={14}>{irritile?.utmZone}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.weather")}>
                                        <Typography fontSize={14}>{irritile?.weatherSources}</Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.created_at")}>
                                        <Typography fontSize={14}>
                                            {formatDate(irritile?.createdAt, dateFormat)}
                                        </Typography>
                                    </KeyValuePair>
                                </Grid>
                                <Grid item xs={12} paddingBottom="0.5rem">
                                    <KeyValuePair header={t("admin.irritile_details_view.updated_at")}>
                                        <Typography fontSize={14}>
                                            {formatDate(irritile?.updatedAt, dateFormat)}
                                        </Typography>
                                    </KeyValuePair>
                                </Grid>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid item xs={9}>
                        <Stack direction="column" gap="0.5rem">
                            <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                                {t("admin.irritile_details_view.orders")}
                            </Typography>
                            <Stack>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{"th, td": {borderColor: "rgba(0, 0, 0, 0.12);"}}}>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.order_id")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.state")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.start_date")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.end_date")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.company_id")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.company_name")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.created_at")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.updated_at")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.activated_at")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        textTransform="uppercase"
                                                        sx={{color: "#737990"}}
                                                        fontSize={14}
                                                    >
                                                        {t("admin.irritile_details_view.flags")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orders?.map((order) => (
                                                <TableRow
                                                    key={order.id}
                                                    sx={{
                                                        "&:last-child td, &:last-child th": {border: 0},
                                                        "th, td": {borderColor: "rgba(0, 0, 0, 0.12);"},
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {order.id}
                                                    </TableCell>
                                                    <TableCell>{order.state}</TableCell>
                                                    <TableCell>{order?.startDate}</TableCell>
                                                    <TableCell>{order?.endDate}</TableCell>
                                                    <TableCell>{order?.companyId}</TableCell>
                                                    <TableCell>
                                                        {
                                                            companies?.data?.find((c) => c.id === order?.companyId)
                                                                ?.properties?.name
                                                        }
                                                    </TableCell>
                                                    <TableCell>{formatDate(order?.createdAt, dateFormat)}</TableCell>
                                                    <TableCell>{formatDate(order?.updatedAt, dateFormat)}</TableCell>
                                                    <TableCell>{formatDate(order?.activatedAt, dateFormat)}</TableCell>
                                                    <TableCell>{order?.flags?.join(", ")}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="column" gap="0.5rem" pl="1rem">
                        <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                            {t("admin.irritile_details_view.fields")}
                        </Typography>
                        <Stack>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{"th, td": {borderColor: "rgba(0, 0, 0, 0.12);"}}}>
                                            <TableCell>
                                                <Typography
                                                    textTransform="uppercase"
                                                    sx={{color: "#737990"}}
                                                    fontSize={14}
                                                >
                                                    {t("admin.irritile_details_view.order_id")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    textTransform="uppercase"
                                                    sx={{color: "#737990"}}
                                                    fontSize={14}
                                                >
                                                    {t("admin.irritile_details_view.field_id")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    textTransform="uppercase"
                                                    sx={{color: "#737990"}}
                                                    fontSize={14}
                                                >
                                                    {t("admin.irritile_details_view.field_name")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    textTransform="uppercase"
                                                    sx={{color: "#737990"}}
                                                    fontSize={14}
                                                >
                                                    {t("admin.irritile_details_view.created_at")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    textTransform="uppercase"
                                                    sx={{color: "#737990"}}
                                                    fontSize={14}
                                                >
                                                    {t("admin.irritile_details_view.updated_at")}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fields.map((field) => (
                                            <TableRow
                                                key={field.id}
                                                sx={{
                                                    "&:last-child td, &:last-child th": {border: 0},
                                                    "th, td": {borderColor: "rgba(0, 0, 0, 0.12);"},
                                                }}
                                            >
                                                <TableCell>{field.orderId}</TableCell>
                                                <TableCell>{field.id}</TableCell>
                                                <TableCell>{field.name}</TableCell>
                                                <TableCell>{formatDate(field?.createdAt, dateFormat)}</TableCell>
                                                <TableCell>{formatDate(field?.updatedAt, dateFormat)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </Stack>
                </Grid>
            </Stack>
        </Stack>
    );
};

const formatDate = (date: string | undefined, dateFormat: string) =>
    date ? formatShortDateTime(dayjs(date), `${dateFormat} HH:mm:ss`) : "";
